import React, { useEffect, useState } from "react"

import { css } from "@emotion/react"
import { useIdleTimer } from "react-idle-timer"

import { AvailableUserIdleState } from "@front-shared/config/general.config.ts"

import { ButtonElement } from "./button.element"
import { PopupWrapperElement } from "./popupWrapper.element"
import { PrimaryText } from "./primaryText.element"

export const UserIdleTimerElement: React.FunctionComponent<
    IUserIdleTimerElementProps
> = props => {
    const [currentIdleState, setCurrentIdleState] =
        useState<AvailableUserIdleState>(AvailableUserIdleState.active)

    const [remainingSeconds, setRemainingSeconds] = useState<number>(
        props.idleTimeout || 60,
    )

    const onIdle = () => {
        setCurrentIdleState(AvailableUserIdleState.idle)

        if (props.isActive) {
            props.onIdle?.()
        }
    }

    const onActive = () => {
        setCurrentIdleState(AvailableUserIdleState.active)

        if (props.isActive) {
            props.onActive?.()
        }
    }

    const onPrompt = () => {
        setCurrentIdleState(AvailableUserIdleState.prompt)
    }

    const { getRemainingTime, activate, pause } = useIdleTimer({
        onIdle,
        onActive,
        onPrompt,
        onAction: props.onAction,
        timeout: (props.idleTimeout || 60) * 1000,
        promptBeforeIdle: (props.promptBeforeIdleTimeout || 10) * 1000, // should be less than timeout
        throttle: 30 * 1000, // shouldn't be set to small values - otherwise it triggered when page just loads
    })

    // Watch for being active and pause / resume + update remaining seconds
    useEffect(() => {
        if (!props.isActive) {
            pause()
        } else {
            activate()

            const interval = setInterval(() => {
                setRemainingSeconds(Math.ceil(getRemainingTime() / 1000))
            }, 500)

            return () => {
                clearInterval(interval)
            }
        }
    }, [props.isActive])

    return (
        <PopupWrapperElement
            isActive={
                props.isActive &&
                !!props.withPrompt &&
                currentIdleState === AvailableUserIdleState.prompt
            }
            onDone={onActive}
        >
            <div className="relative">
                <PrimaryText typography="h3" centered>
                    Are you still here?
                </PrimaryText>

                <PrimaryText typography="text" centered css={styles.subtitle}>
                    Logging out in {remainingSeconds}{" "}
                    {remainingSeconds > 1 ? "seconds" : "second"}
                </PrimaryText>

                <ButtonElement
                    css={styles.button}
                    label="I'm still here"
                    type="primary"
                    size="large"
                    htmlType="button"
                    onClick={activate}
                />
            </div>
        </PopupWrapperElement>
    )
}

const styles = {
    subtitle: css({
        marginTop: 32,
    }),
    button: css({
        marginTop: 32,
        textAlign: "center",
    }),
}

interface IUserIdleTimerElementProps {
    isActive: boolean
    withPrompt?: boolean
    idleTimeout?: number // in seconds
    promptBeforeIdleTimeout?: number // in seconds
    onIdle?: () => void
    onActive?: () => void
    onAction?: () => void
}
